import React, {memo, useEffect, useRef, useState} from 'react';
import {Animated, Easing, Image, Pressable, StyleSheet, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import CCText from '@/Atoms/CCText';
import Shine from '@/Atoms/Shine';
import {Palette} from './campaignConfig';

// 48px checkerboard tile (two white quadrants) — the design's
// repeating-conic-gradient texture, as a tileable SVG so it ports to RN.
// Rendered at low opacity and masked to the right edge (see below).
const CHECKER_URI = `data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48'><rect width='24' height='24' fill='white'/><rect x='24' y='24' width='24' height='24' fill='white'/></svg>",
)}`;

interface CampaignBannerProps {
  palette: Palette;
  kicker: string;
  freeLabel?: string;
  title: string;
  titleAccent?: string;
  subtitle: string;
  detailsText?: string;
  onDetails?: () => void;
  countdownTarget: number | null;
  live?: boolean;
  ctaText: string;
  ctaSubtext?: string;
  onPress: () => void;
  isMobile: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

// Countdown-pill label derived from the ACTUAL target time (not hardcoded) in
// the viewer's local time: "TODAY, 8:00 PM" for tonight, "SAT, 6:30 PM" else.
const DAY3 = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const formatTargetLabel = (ts: number | null): string => {
  if (ts == null) return '';
  const d = new Date(ts);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${isToday ? 'TODAY' : DAY3[d.getDay()]}, ${h}:${pad2(
    d.getMinutes(),
  )} ${ampm}`;
};

// A plain opacity pulse — kept deliberately simpler than the Atoms/RadarDot
// ring-ping (which is sized for a standalone "LIVE" marker) since this dot
// sits inline inside a tight pill next to text.
const PulseDot = ({color, size}: {color: string; size: number}) => {
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.35,
          duration: 800,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: color,
        opacity,
      }}
    />
  );
};

const CampaignBanner = ({
  palette,
  kicker,
  freeLabel,
  title,
  titleAccent,
  subtitle,
  detailsText,
  onDetails,
  countdownTarget,
  live = false,
  ctaText,
  ctaSubtext,
  onPress,
  isMobile,
}: CampaignBannerProps) => {
  const [now, setNow] = useState(() => Date.now());
  // `live` or a null target both render the "no numbers" live state; the pill
  // LABEL itself only flips off `live` (see below) — a scheduled campaign with
  // an unknown target still reads "TODAY, 8:00 PM" with no countdown digits.
  const showLiveState = live || countdownTarget == null;

  useEffect(() => {
    if (showLiveState) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [showLiveState]);

  let countdownStr = '';
  if (!showLiveState && countdownTarget != null) {
    const diff = Math.max(0, countdownTarget - now);
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    countdownStr = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
  }

  const titleFontSize = isMobile ? 22 : 29;
  const titleLineHeight = isMobile ? 25 : 32;

  const titleNode = (
    <CCText
      style={[
        styles.title,
        {color: palette.title, fontSize: titleFontSize, lineHeight: titleLineHeight},
      ]}>
      {title}
      {!!titleAccent && (
        <CCText
          style={[
            styles.title,
            {color: palette.accent, fontSize: titleFontSize, lineHeight: titleLineHeight},
          ]}>
          {' '}
          {titleAccent}
        </CCText>
      )}
    </CCText>
  );

  const pillRow = (
    <View style={styles.pillRow}>
      <View style={[styles.pill, {borderColor: palette.pillBorder}]}>
        <PulseDot color={palette.accent} size={7} />
        <CCText style={[styles.pillText, {color: palette.pillText}]}>
          {kicker}
        </CCText>
      </View>
      {!!freeLabel && (
        <CCText style={[styles.pillText, {color: palette.freeLabel}]}>
          {freeLabel}
        </CCText>
      )}
    </View>
  );

  const subtitleNode = (
    <CCText
      style={[
        styles.subtitle,
        {color: palette.subtitle, fontSize: isMobile ? 13 : 14.5},
      ]}>
      {subtitle}
      {!!detailsText && !!onDetails && (
        <CCText
          onPress={(e: any) => {
            e?.stopPropagation?.();
            onDetails();
          }}
          style={[styles.subtitle, {color: palette.accent, textDecorationLine: 'underline'}]}>
          {' · '}
          {detailsText}
        </CCText>
      )}
    </CCText>
  );

  const countdownPillLabel = live
    ? 'LIVE NOW'
    : formatTargetLabel(countdownTarget);
  const startsInLabel = live ? 'In progress' : 'Starts in';

  const countdownBlock = (
    <View style={styles.countdownColumn}>
      <View
        style={[
          styles.cdPill,
          {backgroundColor: palette.cdPillBg, borderColor: palette.cdPillBorder},
        ]}>
        <PulseDot color={palette.accent} size={6} />
        <CCText style={[styles.cdPillText, {color: palette.cdPillText}]}>
          {countdownPillLabel}
        </CCText>
      </View>
      <CCText style={[styles.cdNum, {color: palette.cdNum}]}>
        {countdownStr}
      </CCText>
      <CCText style={[styles.startsIn, {color: palette.startsIn}]}>
        {startsInLabel}
      </CCText>
    </View>
  );

  const ctaButton = (
    <Pressable
      onPress={(e: any) => {
        e?.stopPropagation?.();
        onPress();
      }}
      style={({hovered, pressed}: any) => [
        styles.cta,
        {backgroundColor: palette.ctaBg},
        {boxShadow: `0 4px 0 ${palette.ctaShadow}`, cursor: 'pointer'} as any,
        hovered && {opacity: 0.94},
        pressed && {opacity: 0.88},
      ]}>
      <Shine width={52} travel={220} interval={2800} intensity={0.5} />
      <View style={styles.ctaRow}>
        <CCText style={[styles.ctaText, {color: palette.ctaText}]}>
          {ctaText}
        </CCText>
        <CCText style={[styles.ctaArrow, {color: palette.ctaText}]}>
          {'→'}
        </CCText>
      </View>
      {!!ctaSubtext && (
        <CCText style={[styles.ctaSubtext, {color: palette.ctaText, opacity: 0.72}]}>
          {ctaSubtext}
        </CCText>
      )}
    </Pressable>
  );

  return (
    <Pressable onPress={onPress} style={{cursor: 'pointer'} as any}>
      <View
        style={[
          styles.outer,
          {backgroundColor: palette.bg},
          {boxShadow: '0 12px 30px rgba(0,0,0,0.22)'} as any,
        ]}>
        {/* Checkerboard texture, faded out toward the left so it only reads on
            the right edge — matches the design's masked conic gradient. */}
        <Image
          source={{uri: CHECKER_URI}}
          resizeMode="repeat"
          style={[StyleSheet.absoluteFill, {opacity: 0.1}]}
          pointerEvents="none"
        />
        <LinearGradient
          colors={[palette.bg, palette.bg, 'transparent']}
          locations={[0, 0.4, 1]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 0}}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
        <LinearGradient
          colors={[palette.accent, palette.accentDark]}
          style={styles.accentBar}
        />
        {isMobile ? (
          <View style={styles.mobileContent}>
            {pillRow}
            {titleNode}
            {subtitleNode}
            <View style={styles.mobileBottomRow}>
              {countdownBlock}
              {ctaButton}
            </View>
          </View>
        ) : (
          <View style={styles.desktopContent}>
            <View style={styles.left}>
              {pillRow}
              {titleNode}
              {subtitleNode}
            </View>
            <View style={styles.right}>
              {countdownBlock}
              {ctaButton}
            </View>
          </View>
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  outer: {
    borderRadius: 18,
    overflow: 'hidden',
    position: 'relative',
    // Fill the carousel slide's fixed height and center the content, so a
    // shorter slide (Puzzle Royale) is the same height as a taller one (arena)
    // instead of leaving a gap under it.
    flex: 1,
    justifyContent: 'center',
    // The gutter between carousel slides, matching the other slide variants
    // in this carousel (see styles.leaderboardBanner in styles.ts).
    marginHorizontal: 8,
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 5,
  },
  desktopContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 30,
    paddingTop: 24,
    paddingBottom: 24,
    paddingLeft: 32,
    paddingRight: 26,
  },
  mobileContent: {
    padding: 20,
    gap: 8,
  },
  left: {
    flex: 1,
    gap: 10,
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 22,
    flexShrink: 0,
  },
  pillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 5,
    paddingLeft: 9,
    paddingRight: 12,
    borderRadius: 7,
    borderWidth: 1,
  },
  pillText: {
    fontSize: 11.5,
    fontWeight: '800',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  title: {
    fontWeight: '900',
    letterSpacing: -0.6,
  },
  subtitle: {
    fontWeight: '600',
    maxWidth: 620,
  },
  countdownColumn: {
    alignItems: 'center',
    gap: 7,
    paddingRight: 22,
    borderRightWidth: 1,
    borderRightColor: 'rgba(255,255,255,0.14)',
  },
  cdPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 4,
    paddingHorizontal: 11,
    borderRadius: 999,
    borderWidth: 1,
  },
  cdPillText: {
    fontSize: 12.5,
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  cdNum: {
    fontSize: 30,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  startsIn: {
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.6,
  },
  cta: {
    alignItems: 'center',
    gap: 2,
    paddingVertical: 13,
    paddingHorizontal: 24,
    borderRadius: 12,
    // So <Shine> is clipped to the pill (its own bottom shadow is drawn
    // outside the box and is unaffected by overflow:hidden).
    position: 'relative',
    overflow: 'hidden',
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  ctaText: {
    fontSize: 16.5,
    fontWeight: '800',
  },
  ctaArrow: {
    fontSize: 18,
  },
  ctaSubtext: {
    fontSize: 11.5,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  mobileBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
  },
});

export default memo(CampaignBanner);
